import React, { useMemo, ReactElement } from 'react';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import FormFieldDate from '../../../components/form-field-date/form-field-date.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import SinglePageTab from '../../app/common/single-page-tab/single-page-tab.component';
import FormatService from '../../../services/format-service';

const AssetInvestmentInformation: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    formError,
    assetData,
    pricingTypeList,
    onSubmitForm,
    onCancelForm,
  } = props;

  const formPrefix: string = 'assetData';
  const parsedFirstPaymentDate: Date = assetData && assetData.firstPaymentDate && new Date(assetData.firstPaymentDate);
  const parsedNextPaymentDueDate: Date = assetData && assetData.nextPaymentDueDate
    && new Date(assetData.nextPaymentDueDate);
  const parsedCurrentCreditLetterCreditExpirationDate: Date = assetData
    && assetData.currentCreditLetterCreditExpirationDate && new Date(assetData.currentCreditLetterCreditExpirationDate);
  const parsedInitialAppraisalDate: Date = assetData && assetData.initialAppraisalDate
    && new Date(assetData.initialAppraisalDate);

  const firstPaymentDate: string = useMemo((): string => (
    parsedFirstPaymentDate && FormatService.formatDateString(parsedFirstPaymentDate)
  ), [parsedFirstPaymentDate]);
  const nextPaymentDueDate: string = useMemo((): string => (
    parsedNextPaymentDueDate && FormatService.formatDateString(parsedNextPaymentDueDate)
  ), [parsedNextPaymentDueDate]);
  const currentCreditLetterCreditExpirationDate: string = useMemo((): string => (
    parsedCurrentCreditLetterCreditExpirationDate
    && FormatService.formatDateString(parsedCurrentCreditLetterCreditExpirationDate)
  ), [parsedCurrentCreditLetterCreditExpirationDate]);
  const initialAppraisalDate: string = useMemo((): string => (
    parsedInitialAppraisalDate && FormatService.formatDateString(parsedInitialAppraisalDate)
  ), [parsedInitialAppraisalDate]);

  const isEmpty: boolean = !isEditMode && !assetData.amountInvested && !assetData.projectedStabilizedNoi
    && !assetData.goingInCapRate && !assetData.untrendedYieldToCost && !assetData.projectedUnleveredIrr
    && !assetData.projectedUnleveredEquityMultiple && !assetData.projectedUnleveredNetProfit
    && !assetData.projectedLeveredIrr && !assetData.projectedLeveredEquityMultiple
    && !assetData.projectedLeveredNetProfit && !assetData.seniorLenderName && !assetData.seniorLoanAmount
    && !assetData.seniorLoanClosingDate && !assetData.seniorLoanMaturityDate && !assetData.seniorLoanInterestRate
    && !assetData.seniorLoanInterestSpread && !assetData.seniorLoanLtv && !assetData.seniorLoanLtc
    && !assetData.mezzanineLenderName && !assetData.mezzanineLoanAmount && !assetData.mezzanineLoanClosingDate
    && !assetData.mezzanineLoanMaturityDate && !assetData.mezzanineLoanInterestRate
    && !assetData.mezzanineLoanLtv && !assetData.mezzanineLoanLtc
    && !assetData.blendedLoanInterestRate && !assetData.blendedLoanInterestSpread && !assetData.firstPaymentDate
    && !assetData.nextPaymentDueDate && !assetData.originalLoanTermMonth && !assetData.loanAge
    && !assetData.remainingTerm && !assetData.amortizationPeriodMonths && !assetData.remainingAmortizationPeriod
    && !assetData.paymentStringYear && !assetData.amortizationType && !assetData.interestOnlyPeriod
    && !assetData.remainingInterestOnlyPeriod && !assetData.interestAccrualMethod && !assetData.primaryLoanPurpose
    && !assetData.underlyingLoanType && !assetData.lienPosition && !assetData.securityInterest
    && !assetData.prepaymentProvisions && !assetData.remainingPrepaymentProvisions
    && !assetData.currentCreditLetterAmount && !assetData.currentCreditLetterCreditExpirationDate
    && !assetData.interestReservesAtOrigination && !assetData.taxInsuranceEscrowsAtOrigination
    && !assetData.capitalExpenditureReservesAtOrigination && !assetData.otherEscrowAndReservesAtOrigination
    && !assetData.currentInterestReserves && !assetData.currentTaxInsuranceEscrows && !assetData.initialAppraisalDate
    && !assetData.currentCapitalExpenditureEscrows && !assetData.currentOtherEscrowsAndReserves;

  const menuItemPricingTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: pricingTypeList,
  }), [pricingTypeList]);

  return (
    <SinglePageTab
      isSaving={isSaving}
      isEmpty={isEmpty}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      {
        (!isEmpty || isEditMode) && (
          <Table>
            <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
              {
                (isEditMode || assetData.amountInvested) && (
                  <TableRow>
                    <TableCell>
                      Amount Invested
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.amountInvested`} />
                        ) : assetData.amountInvested
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.projectedStabilizedNoi) && (
                  <TableRow>
                    <TableCell>
                      Projected Stabilized NOI
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.projectedStabilizedNoi`} />
                        ) : assetData.projectedStabilizedNoi
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.goingInCapRate) && (
                  <TableRow>
                    <TableCell>
                      Going In Cap Rate
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isFloat isBordered name={`${formPrefix}.goingInCapRate`} />
                        ) : assetData.goingInCapRate
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.untrendedYieldToCost) && (
                  <TableRow>
                    <TableCell>
                      Untrended Yield To Cost
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isFloat isBordered name={`${formPrefix}.untrendedYieldToCost`} />
                        ) : assetData.untrendedYieldToCost
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.projectedUnleveredIrr) && (
                  <TableRow>
                    <TableCell>
                      Projected Unlevered IRR
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isFloat isBordered name={`${formPrefix}.projectedUnleveredIrr`} />
                        ) : assetData.projectedUnleveredIrr
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.projectedUnleveredEquityMultiple) && (
                  <TableRow>
                    <TableCell>
                      Projected Unlevered Equity Multiple
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.projectedUnleveredEquityMultiple`} />
                        ) : assetData.projectedUnleveredEquityMultiple
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.projectedUnleveredNetProfit) && (
                  <TableRow>
                    <TableCell>
                      Projected Unlevered Net Profit
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.projectedUnleveredNetProfit`} />
                        ) : assetData.projectedUnleveredNetProfit
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.projectedLeveredIrr) && (
                  <TableRow>
                    <TableCell>
                      Projected Levered IRR
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isFloat isBordered name={`${formPrefix}.projectedLeveredIrr`} />
                        ) : assetData.projectedLeveredIrr
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.projectedLeveredEquityMultiple) && (
                  <TableRow>
                    <TableCell>
                      Projected Levered Equity Multiple
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isFloat isBordered name={`${formPrefix}.projectedLeveredEquityMultiple`} />
                        ) : assetData.projectedLeveredEquityMultiple
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.projectedLeveredNetProfit) && (
                  <TableRow>
                    <TableCell>
                      Projected Levered Net Profit
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.projectedLeveredNetProfit`} />
                        ) : assetData.projectedLeveredNetProfit
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.seniorLenderName) && (
                  <TableRow>
                    <TableCell>
                      Senior Lender Name
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.seniorLenderName`} />
                        ) : assetData.seniorLenderName
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.seniorLoanAmount) && (
                  <TableRow>
                    <TableCell>
                      Senior Loan Amount
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.seniorLoanAmount`} />
                        ) : assetData.seniorLoanAmount
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.seniorLoanClosingDate) && (
                  <TableRow>
                    <TableCell>
                      Senior Loan Closing Date
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldDate isBordered withBlankValues name={`${formPrefix}.seniorLoanClosingDate`} />
                        ) : assetData.seniorLoanClosingDate
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.seniorLoanMaturityDate) && (
                  <TableRow>
                    <TableCell>
                      Senior Loan Maturity Date
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldDate isBordered withBlankValues name={`${formPrefix}.seniorLoanMaturityDate`} />
                        ) : assetData.seniorLoanMaturityDate
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.seniorLoanInterestRate) && (
                  <TableRow>
                    <TableCell>
                      Senior Loan Interest Rate
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldSelect
                            isBordered
                            withBlankElement
                            isDisabled={pricingTypeList && !pricingTypeList.length}
                            name={`${formPrefix}.seniorLoanInterestRate`}
                            optionsTotal={pricingTypeList && pricingTypeList.length}
                          >
                            {menuItemPricingTypeList}
                          </FormFieldSelect>
                        ) : assetData.seniorLoanInterestRateText
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.seniorLoanInterestSpread) && (
                  <TableRow>
                    <TableCell>
                      Senior Loan Interest Spread
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isFloat isBordered name={`${formPrefix}.seniorLoanInterestSpread`} />
                        ) : assetData.seniorLoanInterestSpread
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.seniorLoanLtv) && (
                  <TableRow>
                    <TableCell>
                      Senior Loan LTV
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isFloat isBordered name={`${formPrefix}.seniorLoanLtv`} />
                        ) : assetData.seniorLoanLtv
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.seniorLoanLtc) && (
                  <TableRow>
                    <TableCell>
                      Senior Loan LTC
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isFloat isBordered name={`${formPrefix}.seniorLoanLtc`} />
                        ) : assetData.seniorLoanLtc
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.mezzanineLenderName) && (
                  <TableRow>
                    <TableCell>
                      Mezzanine Lender Name
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.mezzanineLenderName`} />
                        ) : assetData.mezzanineLenderName
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.mezzanineLoanAmount) && (
                  <TableRow>
                    <TableCell>
                      Mezzanine Loan Amount
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.mezzanineLoanAmount`} />
                        ) : assetData.mezzanineLoanAmount
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.mezzanineLoanClosingDate) && (
                  <TableRow>
                    <TableCell>
                      Mezzanine Loan Closing Date
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldDate isBordered withBlankValues name={`${formPrefix}.mezzanineLoanClosingDate`} />
                        ) : assetData.mezzanineLoanClosingDate
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.mezzanineLoanMaturityDate) && (
                  <TableRow>
                    <TableCell>
                      Mezzanine Loan Maturity Date
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldDate isBordered withBlankValues name={`${formPrefix}.mezzanineLoanMaturityDate`} />
                        ) : assetData.mezzanineLoanMaturityDate
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.mezzanineLoanInterestRate) && (
                  <TableRow>
                    <TableCell>
                      Mezzanine Loan Interest Rate
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldSelect
                            isBordered
                            withBlankElement
                            isDisabled={pricingTypeList && !pricingTypeList.length}
                            name={`${formPrefix}.mezzanineLoanInterestRate`}
                            optionsTotal={pricingTypeList && pricingTypeList.length}
                          >
                            {menuItemPricingTypeList}
                          </FormFieldSelect>
                        ) : assetData.mezzanineLoanInterestRateText
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.mezzanineLoanInterestSpread) && (
                  <TableRow>
                    <TableCell>
                      Mezzanine Loan Interest Spread
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isFloat isBordered name={`${formPrefix}.mezzanineLoanInterestSpread`} />
                        ) : assetData.mezzanineLoanInterestSpread
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.mezzanineLoanLtv) && (
                  <TableRow>
                    <TableCell>
                      Mezzanine Loan LTV
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isFloat isBordered name={`${formPrefix}.mezzanineLoanLtv`} />
                        ) : assetData.mezzanineLoanLtv
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.mezzanineLoanLtc) && (
                  <TableRow>
                    <TableCell>
                      Mezzanine Loan LTC
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isFloat isBordered name={`${formPrefix}.mezzanineLoanLtc`} />
                        ) : assetData.mezzanineLoanLtc
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.blendedLoanInterestRate) && (
                  <TableRow>
                    <TableCell>
                      Blended Loan Interest Rate
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldSelect
                            isBordered
                            withBlankElement
                            isDisabled={pricingTypeList && !pricingTypeList.length}
                            name={`${formPrefix}.blendedLoanInterestRate`}
                            optionsTotal={pricingTypeList && pricingTypeList.length}
                          >
                            {menuItemPricingTypeList}
                          </FormFieldSelect>
                        ) : assetData.blendedLoanInterestRateText
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.blendedLoanInterestSpread) && (
                  <TableRow>
                    <TableCell>
                      Blended Loan Interest Spread
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isFloat isBordered name={`${formPrefix}.blendedLoanInterestSpread`} />
                        ) : assetData.blendedLoanInterestSpread
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || firstPaymentDate) && (
                  <TableRow>
                    <TableCell>
                      First Payment Date
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldDate isBordered withBlankValues name={`${formPrefix}.firstPaymentDate`} />
                        ) : firstPaymentDate
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || nextPaymentDueDate) && (
                  <TableRow>
                    <TableCell>
                      Next Payment Due Date
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldDate isBordered withBlankValues name={`${formPrefix}.nextPaymentDueDate`} />
                        ) : nextPaymentDueDate
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.originalLoanTermMonth) && (
                  <TableRow>
                    <TableCell>
                      Original Loan Term Month
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.originalLoanTermMonth`} />
                        ) : assetData.originalLoanTermMonth
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.loanAge) && (
                  <TableRow>
                    <TableCell>
                      Loan Age
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.loanAge`} />
                        ) : assetData.loanAge
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.remainingTerm) && (
                  <TableRow>
                    <TableCell>
                      Remaining Term
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.remainingTerm`} />
                        ) : assetData.remainingTerm
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.amortizationPeriodMonths) && (
                  <TableRow>
                    <TableCell>
                      Amortization Period Months
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.amortizationPeriodMonths`} />
                        ) : assetData.amortizationPeriodMonths
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.remainingAmortizationPeriod) && (
                  <TableRow>
                    <TableCell>
                      Remaining Amortization Period
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.remainingAmortizationPeriod`} />
                        ) : assetData.remainingAmortizationPeriod
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.paymentStringYear) && (
                  <TableRow>
                    <TableCell>
                      Payment String Year
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.paymentStringYear`} />
                        ) : assetData.paymentStringYear
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.amortizationType) && (
                  <TableRow>
                    <TableCell>
                      Amortization Type
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.amortizationType`} />
                        ) : assetData.amortizationType
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.interestOnlyPeriod) && (
                  <TableRow>
                    <TableCell>
                      Interest Only Period
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.interestOnlyPeriod`} />
                        ) : assetData.interestOnlyPeriod
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.remainingInterestOnlyPeriod) && (
                  <TableRow>
                    <TableCell>
                      Remaining Interest Only Period
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.remainingInterestOnlyPeriod`} />
                        ) : assetData.remainingInterestOnlyPeriod
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.interestAccrualMethod) && (
                  <TableRow>
                    <TableCell>
                      Interest Accrual Method
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.interestAccrualMethod`} />
                        ) : assetData.interestAccrualMethod
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.primaryLoanPurpose) && (
                  <TableRow>
                    <TableCell>
                      Primary Loan Purpose
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.primaryLoanPurpose`} />
                        ) : assetData.primaryLoanPurpose
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.underlyingLoanType) && (
                  <TableRow>
                    <TableCell>
                      Underlying Loan Type
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.underlyingLoanType`} />
                        ) : assetData.underlyingLoanType
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.lienPosition) && (
                  <TableRow>
                    <TableCell>
                      Lien Position
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.lienPosition`} />
                        ) : assetData.lienPosition
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.securityInterest) && (
                  <TableRow>
                    <TableCell>
                      Security Interest
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.securityInterest`} />
                        ) : assetData.securityInterest
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.prepaymentProvisions) && (
                  <TableRow>
                    <TableCell>
                      Prepayment Provisions
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.prepaymentProvisions`} />
                        ) : assetData.prepaymentProvisions
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.remainingPrepaymentProvisions) && (
                  <TableRow>
                    <TableCell>
                      Remaining Prepayment Provisions
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.remainingPrepaymentProvisions`} />
                        ) : assetData.remainingPrepaymentProvisions
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.currentCreditLetterAmount) && (
                  <TableRow>
                    <TableCell>
                      Current Credit Letter Amount
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.currentCreditLetterAmount`} />
                        ) : assetData.currentCreditLetterAmount
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || currentCreditLetterCreditExpirationDate) && (
                  <TableRow>
                    <TableCell>
                      Current Credit Letter Credit Expiration Date
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldDate
                            isBordered
                            withBlankValues
                            name={`${formPrefix}.currentCreditLetterCreditExpirationDate`}
                          />
                        ) : currentCreditLetterCreditExpirationDate
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.interestReservesAtOrigination) && (
                  <TableRow>
                    <TableCell>
                      Interest Reserves at Origination
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.interestReservesAtOrigination`} />
                        ) : assetData.interestReservesAtOrigination
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.taxInsuranceEscrowsAtOrigination) && (
                  <TableRow>
                    <TableCell>
                      Tax Insurance Escrows at Origination
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.taxInsuranceEscrowsAtOrigination`} />
                        ) : assetData.taxInsuranceEscrowsAtOrigination
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.capitalExpenditureReservesAtOrigination) && (
                  <TableRow>
                    <TableCell>
                      Cap Ex Reserves at Origination
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.capitalExpenditureReservesAtOrigination`} />
                        ) : assetData.capitalExpenditureReservesAtOrigination
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.otherEscrowAndReservesAtOrigination) && (
                  <TableRow>
                    <TableCell>
                      Other Escrow and Reserves at Origination
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.otherEscrowAndReservesAtOrigination`} />
                        ) : assetData.otherEscrowAndReservesAtOrigination
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.currentInterestReserves) && (
                  <TableRow>
                    <TableCell>
                      Current Interest Reserves
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.currentInterestReserves`} />
                        ) : assetData.currentInterestReserves
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.currentTaxInsuranceEscrows) && (
                  <TableRow>
                    <TableCell>
                      Current Tax Insurance Escrows
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.currentTaxInsuranceEscrows`} />
                        ) : assetData.currentTaxInsuranceEscrows
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.currentCapitalExpenditureEscrows) && (
                  <TableRow>
                    <TableCell>
                      Current Cap Ex Escrows
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.currentCapitalExpenditureEscrows`} />
                        ) : assetData.currentCapitalExpenditureEscrows
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.currentOtherEscrowsAndReserves) && (
                  <TableRow>
                    <TableCell>
                      Current Other Escrows and Reserves
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.currentOtherEscrowsAndReserves`} />
                        ) : assetData.currentOtherEscrowsAndReserves
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || initialAppraisalDate) && (
                  <TableRow>
                    <TableCell>
                      Initial Appraisal Date
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldDate isBordered withBlankValues name={`${formPrefix}.initialAppraisalDate`} />
                        ) : initialAppraisalDate
                      }
                    </TableCell>
                  </TableRow>
                )
              }
            </TableBody>
          </Table>
        )
      }
    </SinglePageTab>
  );
});

export default AssetInvestmentInformation;
